import React, { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { get } from 'services/network'
import {
  getAccessToken,
  getOrgIdFromStorage,
  getOrgNameFromStorage,
  setOrganizationInStorage,
} from 'utils/common'
import Logo from 'assets/images/shell/logo.svg'
import DashboardIcon from 'assets/images/shell/nav-dashboard.svg'
import UsersIcon from 'assets/images/shell/nav-users.svg'
import TeamsIcon from 'assets/images/shell/nav-teams.svg'
import EventsIcon from 'assets/images/shell/nav-events.svg'
import NotificationsIcon from 'assets/images/shell/nav-notifications.svg'
import OrganizationsIcon from 'assets/images/shell/nav-organizations.svg'
import ChevronUp from 'assets/images/shell/chevron-up.svg'
import CollapseIcon from 'assets/images/shell/icon-collapse.svg'
import BuildingIcon from 'assets/images/shell/nav-organizations.svg'
import './Sidebar.scss'

const NAV_ITEMS = [
  { to: '/home', label: 'Dashboard', icon: DashboardIcon },
  { to: '/users', label: 'Users', icon: UsersIcon },
  { to: '/teams', label: 'Teams', icon: TeamsIcon },
  { to: '/events', label: 'Events', icon: EventsIcon },
  { to: '/notifications', label: 'Notifications', icon: NotificationsIcon },
  { to: '/organizations', label: 'Organizations', icon: OrganizationsIcon },
]

const getOrganizationLabel = (organization) =>
  organization?.org_name ||
  organization?.name ||
  organization?.organizationName ||
  'Organization'

function Sidebar({ collapsed, onToggle }) {
  const location = useLocation()
  const orgMenuRef = useRef(null)
  const [organizationName, setOrganizationName] = useState(
    () => getOrgNameFromStorage() || 'Select organization'
  )
  const [organizations, setOrganizations] = useState([])
  const [menuOpen, setMenuOpen] = useState(false)

  const resolveOrganizationName = useCallback(async () => {
    const organizationId = getOrgIdFromStorage()
    const storedName = getOrgNameFromStorage()

    if (!organizationId) {
      setOrganizationName('Select organization')
      return
    }

    if (storedName) {
      setOrganizationName(storedName)
      return
    }

    try {
      const response = await get('organization', false, null, null, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      const payload = Array.isArray(response)
        ? response
        : response?.organizations || response?.data || []
      const match = payload.find(
        (item) =>
          String(item.organizationId || item.id || '') === organizationId
      )

      if (match) {
        const label = getOrganizationLabel(match)
        setOrganizationInStorage(organizationId, label)
        setOrganizationName(label)
        return
      }
    } catch {
      // Keep the fallback label below.
    }

    setOrganizationName('Organization')
  }, [])

  const loadOrganizations = useCallback(async () => {
    try {
      const response = await get('organization', false, null, null, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      const payload = Array.isArray(response)
        ? response
        : response?.organizations || response?.data || []
      setOrganizations(
        payload
          .filter((item) => item.enabled !== false)
          .map((item) => ({
            id: String(item.organizationId || item.id || ''),
            name: getOrganizationLabel(item),
          }))
          .filter((item) => item.id)
      )
    } catch {
      setOrganizations([])
    }
  }, [])

  useEffect(() => {
    const storedName = getOrgNameFromStorage()
    if (storedName) {
      setOrganizationName(storedName)
    }
    resolveOrganizationName()
  }, [location.pathname, resolveOrganizationName])

  useEffect(() => {
    if (!menuOpen) return undefined

    const closeOnOutsideClick = (event) => {
      if (!orgMenuRef.current?.contains(event.target)) {
        setMenuOpen(false)
      }
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [menuOpen])

  const selectOrganization = (organization) => {
    if (organization.id === getOrgIdFromStorage()) {
      setMenuOpen(false)
      return
    }

    setOrganizationInStorage(organization.id, organization.name)
    setOrganizationName(organization.name)
    setMenuOpen(false)
    window.location.reload()
  }

  return (
    <aside className={`sidebar-main ${collapsed ? 'sidebar-main--collapsed' : ''}`}>
      <div className='sidebar-brand'>
        <img src={Logo} width={104} height={30} alt='XLR8' />
      </div>
      <nav className='sidebar-nav'>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `sidebar-link${isActive ? ' sidebar-link--active' : ''}`
            }
          >
            <span
              className='sidebar-link__icon'
              style={{
                maskImage: `url(${item.icon})`,
                WebkitMaskImage: `url(${item.icon})`,
              }}
            />
            <span className='sidebar-link__label'>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className='sidebar-footer' ref={orgMenuRef}>
        {menuOpen && (
          <div className='sidebar-org-menu' role='listbox' aria-label='Organisations'>
            {organizations.length === 0 ? (
              <p className='sidebar-org-menu__empty'>No organisations found</p>
            ) : (
              organizations.map((organization) => {
                const isSelected = organization.id === getOrgIdFromStorage()
                return (
                  <button
                    key={organization.id}
                    type='button'
                    role='option'
                    aria-selected={isSelected}
                    className={
                      isSelected
                        ? 'sidebar-org-menu__option sidebar-org-menu__option--selected'
                        : 'sidebar-org-menu__option'
                    }
                    onClick={() => selectOrganization(organization)}
                  >
                    {organization.name}
                  </button>
                )
              })
            )}
          </div>
        )}
        <button
          type='button'
          className='sidebar-org'
          aria-expanded={menuOpen}
          aria-haspopup='listbox'
          onClick={() => {
            setMenuOpen((open) => {
              const nextOpen = !open
              if (nextOpen) loadOrganizations()
              return nextOpen
            })
          }}
        >
          <span className='sidebar-org__avatar'>
            <img src={BuildingIcon} width={20} height={20} alt='' />
          </span>
          <span className='sidebar-org__copy'>
            <span className='sidebar-org__label'>Select Organisation</span>
            <span className='sidebar-org__name' title={organizationName}>
              {organizationName}
            </span>
          </span>
          <img
            className={
              menuOpen ? 'sidebar-org__chevron sidebar-org__chevron--open' : 'sidebar-org__chevron'
            }
            src={ChevronUp}
            width={20}
            height={20}
            alt=''
          />
        </button>
        <p className='sidebar-powered'>Powered by Ascend Solutions</p>
      </div>
      <button
        type='button'
        className='sidebar-collapse'
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        onClick={onToggle}
      >
        <img src={CollapseIcon} width={24} height={24} alt='' />
      </button>
    </aside>
  )
}

Sidebar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
}

export default Sidebar
